import React from 'react';
import { useStoreState } from '@/store';

// Internal modules
import { features, useMountLogging } from '@/lib';
import { FormContainer } from './StyledComponents';
import {
  LazyAddressNormalizedAddressInterstitial,
  LazyGenericError,
  LazyInstallmentInterstitial,
  LazyOneTimePasswordConfirmationInterstitial,
  LazyPasswordlessUpgradeInterstitial,
  LazyPhoneConfirmationInterstitial,
  LazySepaInterstitial,
  LazyThreeDS2Interstitial,
  LazyUpgradeInterstitial,
  // @ts-expect-error untyped import
} from './Components';
import { PayNowForm } from './PayNowForm';
import { LazyFILimitExceededInterstitial } from '../Interstitials/FILimitExceededInterstitial';
import { LazyMemberOTPInterstitial } from '../Interstitials/MemberOTPInterstitial';
import { SpinnerRender } from '../Common/Spinner/SpinnerRender';
import { formInputHandlers } from './formInputHandlers';
import { TwoTabsComponent } from './TabsComponent/TwoTabsComponent';
import {
  atomics,
  atomicsDomain,
  atomicsStatus,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';

export const Form = (): JSX.Element => {
  useMountLogging('Form');

  const showTabs = useStoreState(({ ui }) => ui.showTabs);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const isGriffinMetadataLoaded = useStoreState(({ api }) => {
    return Boolean(Object.keys(api?.griffinMetadata?.address || {}).length > 0);
  });

  const isInstallmentInterstitialShown = useStoreState(
    ({ ui }) => ui.isInstallmentInterstitialShown,
  );

  // TODO: rewrite this logic to have a single entry-point and
  // handler for both 3DS 1.0 and 2.0
  const isThreeDS2Shown = useStoreState(({ ui }) => ui.isThreeDS2Shown);

  const isAddressNormalizeOpen = useStoreState(
    ({ ui }) => ui.isAddressNormalizeOpen,
  );
  const isSepaInterstitialOpen = useStoreState(
    ({ ui }) => ui.isSepaInterstitialOpen,
  );
  const isUpgradeInterstitialOpen = useStoreState(
    ({ ui }) => ui.isUpgradeInterstitialOpen,
  );
  const isPhoneConfirmationOpen = useStoreState(
    ({ ui }) => ui.isPhoneConfirmationOpen,
  );
  const isOneTimePasswordConfirmationOpen = useStoreState(
    ({ ui }) => ui.isOneTimePasswordConfirmationOpen,
  );

  const isPasswordlessUpgradeOpen = useStoreState(
    ({ ui }) => ui.isPasswordlessUpgradeOpen,
  );
  const showMemberOTPOpen = useStoreState(
    ({ ui }) =>
      ui.identityAuthProviderStatus === 'eligible' &&
      (ui.hasFormBeenFocused || ui.isAutoSmsNotified),
  );
  const showFILimitExceededOpen = useStoreState(
    ({ ui }) => ui.isFILimitExceededInterstitialOpen,
  );
  const genericError = useStoreState(({ ui }) => ui.genericError);

  const shouldSuppressSMSOTPLogin = features.isActive(
    'shouldSuppressSMSOTPLogin',
  );

  return (
    <FormContainer data-testid="weasley-form-container">
      {showTabs ? <TwoTabsComponent /> : <PayNowForm />}
      {!!genericError && <LazyGenericError error={genericError} />}
      {isThreeDS2Shown && <LazyThreeDS2Interstitial />}
      {isAddressNormalizeOpen && <LazyAddressNormalizedAddressInterstitial />}
      {isInstallmentInterstitialShown && <LazyInstallmentInterstitial />}
      {isSepaInterstitialOpen && <LazySepaInterstitial />}
      {isPhoneConfirmationOpen && <LazyPhoneConfirmationInterstitial />}
      {isOneTimePasswordConfirmationOpen && (
        <LazyOneTimePasswordConfirmationInterstitial />
      )}
      {isUpgradeInterstitialOpen && (
        <LazyUpgradeInterstitial {...formInputHandlers} />
      )}
      {isPasswordlessUpgradeOpen && (
        <LazyPasswordlessUpgradeInterstitial {...formInputHandlers} />
      )}
      {showFILimitExceededOpen && <LazyFILimitExceededInterstitial />}
      {showMemberOTPOpen && !shouldSuppressSMSOTPLogin && (
        <LazyMemberOTPInterstitial />
      )}
      {isFormSubmitting && (
        <SpinnerRender
          containToParent={false}
          overlayStyleOverrides={{
            backgroundColor: '#fff',
            opacity: 0.9,
            zIndex: 1089,
          }}
        />
      )}
      {!isFormSubmitting && isGriffinMetadataLoaded && (
        <atomics.MarkUIReady
          viewName={atomicsViewNames.reviewYourPayment}
          status={atomicsStatus.ok}
          domain={atomicsDomain}
          task={atomicsTasks.reviewYourPayment}
        />
      )}
    </FormContainer>
  );
};
